import { useCreateEventdetector, useShapeFetch } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useGetFilters } from '@ais-ui/websockets';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { AreaDetectorDialog } from './AreaDetectorDialog';
import {
  areaDetectorFormSchema,
  AreaDetectorFormValues,
  convertFormValuesToBackend,
} from './areaForm';
import { DetectorType } from '../Mutations/detectorForm';

interface CreateDetectorDialogProps {
  detectorType: DetectorType;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const CreateAreaDetectorDialog = ({
  detectorType,
  showDialog,
  setShowDialog,
}: CreateDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate } = useCreateEventdetector();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shapeFetch = useShapeFetch();
  const { toast } = useToast();
  const { data: filters } = useGetFilters();
  const form = useForm<AreaDetectorFormValues>({
    resolver: zodResolver(areaDetectorFormSchema),
    defaultValues: {
      enabled: true,
      selectedGroups: [],
      severity: 'note',
      filterId: 'no-filter',
      maintainShiplist: false,
      acknowledgeRequired: false,
      timeBeforeActivation: undefined,
      timeBeforeDeactivation: undefined,
      timeBetweenTrigger: undefined,
      timeoutWhenActive: undefined,
      activityFilters: [],
      sendShipNotification: false,
      shipNotificationText: '',
      useVesselDimensions: false,
      entireVesselInside: false,
      navigationStatusTriggerThreshold: 0,
      navigationStatusIgnoreThreshold: 0,
      driftingSpeedOverGroundThreshold: 0,
      driftingAngleThreshold: 0,
      driftingTimeThreshold: 0,
      exitCheckThreshold: 0,
      exitCheckTriggerLost: false,
      deltaSpeedOverGroundThreshold: 0,
      emails: [],
      phones: [],
      circleCenterCpa: true,
      checkCpa: false,
      cpaLimit: 0,
      tcpaLimit: 0,
    },
  });

  const onCreate = useCallback(
    async (data: AreaDetectorFormValues) => {
      if (!filters) {
        return;
      }
      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }

      const filter = filters.find((filter) => filter.uuid === data.filterId);

      const backendValues = convertFormValuesToBackend(
        detectorType,
        data,
        shape,
        filter
      );

      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'create',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      mutate(
        {
          eventdetectorDefinition: backendValues,
          dc: 1707829314034,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutate, detectorType]
  );

  return (
    <AreaDetectorDialog
      form={form}
      onSubmit={onCreate}
      setShowOuterDialog={setShowDialog}
      mutateType="create"
      isSubmitting={isSubmitting}
    ></AreaDetectorDialog>
  );
};
